<template>
  <div class="past-subscriptions">
    <v-container fluid class="px-6 py-4">
      <!-- Page Header -->
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="page-title-section">
            <div class="d-flex align-center mb-2">
              <h1 class="page-title">
                <v-icon large class="vibrant-title-icon mr-3">mdi-history</v-icon>
                Past Subscriptions
                <span class="page-inline-subtitle">View all cancelled, expired, and paused subscription plans</span>
              </h1>
              <v-spacer></v-spacer>
              <div class="header-actions">
                <v-btn
                  color="success"
                  to="/active-subscriptions"
                  class="modern-btn"
                  elevation="2"
                >
                  <v-icon left>mdi-shield-check</v-icon>
                  View Active
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Stats Cards -->
      <v-row class="mb-6">
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="4">
            <v-card-text class="pa-4">
              <div class="d-flex align-center">
                <div class="stats-icon-wrapper error">
                  <v-icon color="white">mdi-cancel</v-icon>
                </div>
                <div class="ml-3">
                  <div class="stats-value">{{ stats.cancelled_subscriptions }}</div>
                  <div class="stats-label">Cancelled</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="4">
            <v-card-text class="pa-4">
              <div class="d-flex align-center">
                <div class="stats-icon-wrapper warning">
                  <v-icon color="white">mdi-pause</v-icon>
                </div>
                <div class="ml-3">
                  <div class="stats-value">{{ stats.paused_subscriptions }}</div>
                  <div class="stats-label">Paused</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="4">
            <v-card-text class="pa-4">
              <div class="d-flex align-center">
                <div class="stats-icon-wrapper grey">
                  <v-icon color="white">mdi-clock-outline</v-icon>
                </div>
                <div class="ml-3">
                  <div class="stats-value">{{ stats.expired_subscriptions }}</div>
                  <div class="stats-label">Expired</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="4">
            <v-card-text class="pa-4">
              <div class="d-flex align-center">
                <div class="stats-icon-wrapper info">
                  <v-icon color="white">mdi-currency-usd-off</v-icon>
                </div>
                <div class="ml-3">
                  <div class="stats-value">${{ stats.lost_revenue }}</div>
                  <div class="stats-label">Lost Revenue/mo</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Filters -->
      <v-card class="filters-card mb-6" elevation="4">
        <v-card-text class="pa-4">
          <v-row align="center">
            <v-col cols="12" md="3">
              <v-text-field
                v-model="filters.search"
                outlined
                label="Search subscriptions..."
                prepend-inner-icon="mdi-magnify"
                clearable
                dense
                class="minimal-input"
                @input="debounceFilter"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="2">
              <v-select
                v-model="filters.plan_type"
                outlined
                label="Plan Type"
                :items="planTypes"
                clearable
                dense
                class="minimal-input"
                @change="fetchSubscriptions"
              ></v-select>
            </v-col>
            <v-col cols="12" md="2">
              <v-select
                v-model="filters.status"
                outlined
                label="Status"
                :items="statusOptions"
                clearable
                dense
                class="minimal-input"
                @change="fetchSubscriptions"
              ></v-select>
            </v-col>
            <v-col cols="12" md="2">
              <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="filters.date_range"
                    label="Date Range"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    outlined
                    dense
                    class="minimal-input"
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="dateRange"
                  range
                  @change="updateDateFilter"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" md="3">
              <div class="filter-actions">
                <v-btn
                  color="primary"
                  @click="resetFilters"
                  outlined
                  class="mr-2"
                >
                  Reset
                </v-btn>
                <v-btn
                  v-if="subscriptions.length > 0"
                  color="success"
                  @click="exportData"
                  :loading="isExporting"
                >
                  <v-icon left small>mdi-download</v-icon>
                  Export
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Subscriptions Table -->
      <v-card class="subscriptions-table-card" elevation="8">
        <v-card-title class="table-header">
          <v-icon class="mr-2">mdi-table</v-icon>
          Past Subscription Details
          <v-spacer></v-spacer>
          <div class="table-count" v-if="totalSubscriptions > 0">
            <span class="count-text">{{ totalSubscriptions }} subscription{{ totalSubscriptions !== 1 ? 's' : '' }}</span>
          </div>
        </v-card-title>
        
        <v-data-table
          :headers="headers"
          :items="subscriptions"
          :loading="isLoading"
          :options.sync="tableOptions"
          :server-items-length="totalSubscriptions"
          class="subscription-table"
          item-key="id"
        >
          <!-- Company Name -->
          <template slot="item.company_name" slot-scope="{ item }">
            <div class="company-info">
              <v-avatar size="24" class="mr-2">
                <v-icon small>mdi-office-building</v-icon>
              </v-avatar>
              <span class="company-name">{{ item.company_name || 'Unknown Company' }}</span>
            </div>
          </template>

          <!-- Subscription Plan -->
          <template slot="item.subscription_plan" slot-scope="{ item }">
            <div class="plan-info">
              <div class="plan-name">{{ item.subscription_plan?.name || 'Unknown Plan' }}</div>
              <div class="plan-type">{{ formatPlanType(item.subscription_plan?.type || '') }}</div>
            </div>
          </template>

          <!-- AI Agents -->
          <template slot="item.ai_agents" slot-scope="{ item }">
            <div class="agents-list">
              <v-chip
                v-for="agent in (item.subscription_plan?.agents || []).slice(0, 2)"
                :key="agent.ai_agent?.id || Math.random()"
                small
                class="mr-1 mb-1"
                color="primary"
                outlined
              >
                {{ agent.ai_agent?.name || 'Unknown Agent' }}
              </v-chip>
              <v-chip
                v-if="(item.subscription_plan?.agents || []).length > 2"
                small
                class="mr-1 mb-1"
                color="grey"
                outlined
              >
                +{{ (item.subscription_plan?.agents || []).length - 2 }} more
              </v-chip>
            </div>
          </template>

          <!-- Billing -->
          <template slot="item.billing" slot-scope="{ item }">
            <div class="billing-info">
              <div class="amount">${{ item.subscription_plan?.monthly_price || 0 }}/mo</div>
              <div class="billing-cycle">{{ item.billing_cycle || 'Monthly' }}</div>
            </div>
          </template>

          <!-- Status -->
          <template slot="item.status" slot-scope="{ item }">
            <v-chip
              :color="getStatusColor(item.status)"
              dark
              small
            >
              {{ formatStatus(item.status) }}
            </v-chip>
          </template>

          <!-- Start Date -->
          <template slot="item.start_date" slot-scope="{ item }">
            <div class="date-info">
              {{ formatDate(item.start_date) }}
            </div>
          </template>

          <!-- End Date -->
          <template slot="item.end_date" slot-scope="{ item }">
            <div class="date-info">
              <span v-if="item.end_date">{{ formatDate(item.end_date) }}</span>
              <span v-else class="text-muted">-</span>
            </div>
          </template>

          <!-- Duration -->
          <template slot="item.duration" slot-scope="{ item }">
            <div class="duration-info">
              {{ calculateDuration(item.start_date, item.end_date) }}
            </div>
          </template>

          <!-- Reason -->
          <template slot="item.reason" slot-scope="{ item }">
            <div class="reason-info">
              <v-tooltip bottom v-if="item.notes">
                <template v-slot:activator="{ on, attrs }">
                  <v-chip
                    small
                    color="info"
                    outlined
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon small left>mdi-information</v-icon>
                    {{ getReasonText(item.status, item.notes) }}
                  </v-chip>
                </template>
                <span>{{ item.notes }}</span>
              </v-tooltip>
              <span v-else class="text-muted">{{ getReasonText(item.status) }}</span>
            </div>
          </template>

          <!-- Actions -->
          <template slot="item.actions" slot-scope="{ item }">
            <div class="action-buttons">
              <v-btn
                icon
                small
                color="primary"
                @click="viewSubscription(item)"
                :title="'View subscription details'"
              >
                <v-icon small>mdi-eye</v-icon>
              </v-btn>
              <v-btn
                v-if="item.status === 'paused'"
                icon
                small
                color="success"
                @click="reactivateSubscription(item)"
                :title="'Reactivate subscription'"
              >
                <v-icon small>mdi-play</v-icon>
              </v-btn>
              <v-btn
                v-if="item.status !== 'cancelled'"
                icon
                small
                color="warning"
                @click="editSubscription(item)"
                :title="'Edit subscription'"
              >
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card>

      <!-- Empty State -->
      <div v-if="!isLoading && subscriptions.length === 0" class="empty-state">
        <v-icon size="64" color="#64748b">mdi-history</v-icon>
        <h2>No Past Subscriptions</h2>
        <p>No past subscriptions match your current filters</p>
        <v-btn color="primary" @click="resetFilters" class="mt-4">
          <v-icon left>mdi-refresh</v-icon>
          Reset Filters
        </v-btn>
      </div>
    </v-container>

    <!-- Edit Subscription Dialog -->
    <v-dialog v-model="showEditDialog" max-width="600px" persistent>
      <v-card class="edit-dialog" elevation="24">
        <v-card-title class="dialog-header">
          <div class="dialog-header-content">
            <div class="dialog-title-section">
              <h2 class="dialog-title">Edit Past Subscription</h2>
              <p class="dialog-subtitle">Update subscription details for {{ editingSubscription?.company_name }}</p>
            </div>
            <v-btn 
              icon 
              @click="closeEditDialog"
              class="close-btn"
            >
              <v-icon color="white">mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text class="dialog-content pa-6">
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="editForm.end_date"
                outlined
                label="End Date"
                prepend-inner-icon="mdi-calendar"
                :error-messages="editErrors.end_date"
                class="minimal-input"
                dense
                type="date"
                hide-details="auto"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="editForm.status"
                outlined
                label="Status"
                :items="statusOptionsForEdit"
                :error-messages="editErrors.status"
                class="minimal-input"
                dense
                hide-details="auto"
              ></v-select>
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="editForm.notes"
                outlined
                label="Notes / Cancellation Reason"
                prepend-inner-icon="mdi-note-text"
                :error-messages="editErrors.notes"
                class="minimal-input"
                dense
                rows="3"
                hide-details="auto"
                placeholder="Add notes about why this subscription was cancelled, paused, or expired..."
              ></v-textarea>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="dialog-actions pa-6">
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="closeEditDialog"
            class="cancel-btn"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="updateSubscription"
            :loading="isUpdating"
            class="save-btn"
          >
            Update Subscription
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "@/axios";

export default {
  name: "PastSubscriptions",
  data() {
    return {
      subscriptions: [],
      isLoading: false,
      isExporting: false,
      totalSubscriptions: 0,
      searchTimeout: null,
      dateMenu: false,
      dateRange: [],
      tableOptions: {
        page: 1,
        itemsPerPage: 15,
        sortBy: ['end_date'],
        sortDesc: [true]
      },
      filters: {
        search: '',
        plan_type: '',
        status: '',
        date_range: ''
      },
      stats: {
        cancelled_subscriptions: 0,
        paused_subscriptions: 0,
        expired_subscriptions: 0,
        lost_revenue: 0
      },
      headers: [
        { text: 'Company', value: 'company_name', sortable: true },
        { text: 'Plan', value: 'subscription_plan', sortable: false },
        { text: 'AI Agents', value: 'ai_agents', sortable: false },
        { text: 'Billing', value: 'billing', sortable: true },
        { text: 'Status', value: 'status', sortable: true },
        { text: 'Start Date', value: 'start_date', sortable: true },
        { text: 'End Date', value: 'end_date', sortable: true },
        { text: 'Duration', value: 'duration', sortable: false },
        { text: 'Reason', value: 'reason', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false, width: '120' }
      ],
      planTypes: [
        { text: 'Starter', value: 'starter' },
        { text: 'Professional', value: 'professional' },
        { text: 'Enterprise', value: 'enterprise' },
        { text: 'Custom', value: 'custom' }
      ],
      statusOptions: [
        { text: 'Cancelled', value: 'cancelled' },
        { text: 'Paused', value: 'paused' },
        { text: 'Expired', value: 'expired' }
      ],
      statusOptionsForEdit: [
        { text: 'Active', value: 'active' },
        { text: 'Paused', value: 'paused' },
        { text: 'Cancelled', value: 'cancelled' },
        { text: 'Expired', value: 'expired' }
      ],
      showEditDialog: false,
      editingSubscription: null,
      isUpdating: false,
      editForm: {
        end_date: '',
        status: '',
        notes: ''
      },
      editErrors: {}
    };
  },
  watch: {
    tableOptions: {
      handler() {
        this.fetchSubscriptions();
      },
      deep: true
    }
  },
  mounted() {
    this.fetchSubscriptions();
  },
  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
  methods: {
    async fetchSubscriptions() {
      this.isLoading = true;
      try {
        // Build query parameters for filtering
        const params = {
          page: this.tableOptions.page,
          rowsPerPage: this.tableOptions.itemsPerPage
        };
        
        // Add filters to params
        if (this.filters.search) {
          params.search_keyword = this.filters.search;
        }
        
        if (this.filters.status) {
          params.status = this.filters.status;
        } else {
          // Default to past subscriptions (not active)
          params.status_not = 'active';
        }
        
        if (this.filters.plan_type) {
          params.plan_type = this.filters.plan_type;
        }

        if (this.filters.date_range) {
          params.date_range = this.filters.date_range;
        }

        // Fetch past company subscriptions from the backend
        const response = await axios.get('/company-subscriptions', { params });
        
        // Transform company subscriptions data for the table
        this.subscriptions = response.data.data
          .filter(sub => sub.status !== 'active') // Ensure we only show past subscriptions
          .map(companySubscription => ({
            id: companySubscription.id,
            company_id: companySubscription.company_id,
            company_name: companySubscription.company?.name || 'Unknown Company',
            subscription_plan: companySubscription.subscription_plan || companySubscription.subscriptionPlan,
            billing_cycle: 'Monthly', 
            status: companySubscription.status,
            start_date: companySubscription.start_date,
            end_date: companySubscription.end_date,
            coupon_code: companySubscription.coupon_code,
            is_custom: companySubscription.is_custom,
            notes: companySubscription.notes,
          }));
        
        this.totalSubscriptions = this.subscriptions.length;
        
        // Fetch stats after getting subscriptions
        await this.fetchStats();
      } catch (error) {
        console.error('Error fetching past subscriptions:', error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
        // Show demo data if API fails
        this.subscriptions = this.generateDemoData();
        this.totalSubscriptions = this.subscriptions.length;
        await this.fetchStats();
      }
      this.isLoading = false;
    },
    
    generateDemoData() {
      return [
        {
          id: 1,
          company_id: 1,
          company_name: 'Demo Company 1',
          subscription_plan: {
            name: 'Professional Plan',
            type: 'professional',
            monthly_price: 299,
            agents: [{ ai_agent: { name: 'ContentMod AI' } }]
          },
          billing_cycle: 'Monthly',
          status: 'cancelled',
          start_date: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
          end_date: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
          notes: 'Budget constraints'
        },
        {
          id: 2,
          company_id: 2,
          company_name: 'Demo Company 2',
          subscription_plan: {
            name: 'Enterprise Plan',
            type: 'enterprise',
            monthly_price: 899,
            agents: [
              { ai_agent: { name: 'ContentMod AI' } },
              { ai_agent: { name: 'Analytics AI' } }
            ]
          },
          billing_cycle: 'Monthly',
          status: 'expired',
          start_date: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(),
          end_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          notes: 'Contract ended'
        }
      ];
    },

    async fetchStats() {
      try {
        // Calculate stats from the subscriptions data
        const cancelledSubs = this.subscriptions.filter(s => s.status === 'cancelled');
        const pausedSubs = this.subscriptions.filter(s => s.status === 'paused');
        const expiredSubs = this.subscriptions.filter(s => s.status === 'expired');
        
        const lostRevenue = this.subscriptions.reduce((sum, s) => {
          const price = s.subscription_plan?.monthly_price || 0;
          return sum + parseFloat(price);
        }, 0);
        
        this.stats = {
          cancelled_subscriptions: cancelledSubs.length,
          paused_subscriptions: pausedSubs.length,
          expired_subscriptions: expiredSubs.length,
          lost_revenue: lostRevenue.toFixed(0)
        };
      } catch (error) {
        console.error('Error fetching stats:', error);
        this.stats = {
          cancelled_subscriptions: 0,
          paused_subscriptions: 0,
          expired_subscriptions: 0,
          lost_revenue: 0
        };
      }
    },

    resetFilters() {
      this.filters = {
        search: '',
        plan_type: '',
        status: '',
        date_range: ''
      };
      this.dateRange = [];
      this.tableOptions.page = 1;
      this.fetchSubscriptions();
    },

    debounceFilter() {
      this.tableOptions.page = 1;
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.fetchSubscriptions();
      }, 500);
    },

    updateDateFilter() {
      if (this.dateRange.length === 2) {
        this.filters.date_range = `${this.dateRange[0]} to ${this.dateRange[1]}`;
        this.fetchSubscriptions();
      }
    },

    formatPlanType(type) {
      if (!type) return '';
      return type.charAt(0).toUpperCase() + type.slice(1);
    },

    formatStatus(status) {
      return status.charAt(0).toUpperCase() + status.slice(1);
    },

    getStatusColor(status) {
      const colors = {
        cancelled: 'error',
        paused: 'warning',
        expired: 'grey'
      };
      return colors[status] || 'grey';
    },

    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString();
    },

    calculateDuration(startDate, endDate) {
      if (!startDate || !endDate) return '-';
      
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays < 30) {
        return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
      } else if (diffDays < 365) {
        const months = Math.floor(diffDays / 30);
        return `${months} month${months !== 1 ? 's' : ''}`;
      } else {
        const years = Math.floor(diffDays / 365);
        const remainingMonths = Math.floor((diffDays % 365) / 30);
        return `${years}y ${remainingMonths}m`;
      }
    },

    getReasonText(status, notes) {
      if (notes) {
        return notes.length > 20 ? notes.substring(0, 20) + '...' : notes;
      }
      
      const defaultReasons = {
        cancelled: 'Cancelled',
        paused: 'Paused',
        expired: 'Expired'
      };
      
      return defaultReasons[status] || 'Unknown';
    },

    viewSubscription(subscription) {
      if (subscription.subscription_plan?.id) {
        this.$router.push(`/subscriptions/${subscription.subscription_plan.id}`);
      }
    },

    editSubscription(subscription) {
      this.editingSubscription = subscription;
      this.editForm = {
        end_date: subscription.end_date ? subscription.end_date.split('T')[0] : '',
        status: subscription.status,
        notes: subscription.notes || ''
      };
      this.editErrors = {};
      this.showEditDialog = true;
    },

    closeEditDialog() {
      this.showEditDialog = false;
      this.editingSubscription = null;
      this.editForm = {
        end_date: '',
        status: '',
        notes: ''
      };
      this.editErrors = {};
    },

    async updateSubscription() {
      try {
        this.isUpdating = true;
        this.editErrors = {};
        
        await axios.put(`/company-subscriptions/${this.editingSubscription.id}`, this.editForm);
        
        this.$emit('show-message', {
          type: 'success',
          message: 'Subscription updated successfully'
        });
        
        this.closeEditDialog();
        await this.fetchSubscriptions();
      } catch (error) {
        if (error.response && error.response.data && error.response.data.errors) {
          this.editErrors = error.response.data.errors;
        }
        console.error('Error updating subscription:', error);
        this.$emit('show-message', {
          type: 'error',
          message: 'Failed to update subscription. Please try again.'
        });
      }
      this.isUpdating = false;
    },

    async reactivateSubscription(subscription) {
      if (confirm(`Are you sure you want to reactivate the subscription for ${subscription.company_name}?`)) {
        try {
          await axios.put(`/company-subscriptions/${subscription.id}`, {
            status: 'active',
            end_date: null,
            notes: (subscription.notes || '') + ' - Reactivated on ' + new Date().toLocaleDateString()
          });
          
          this.$emit('show-message', {
            type: 'success',
            message: 'Subscription reactivated successfully'
          });
          
          await this.fetchSubscriptions();
        } catch (error) {
          console.error('Error reactivating subscription:', error);
          this.$emit('show-message', {
            type: 'error',
            message: 'Failed to reactivate subscription. Please try again.'
          });
        }
      }
    },

    async exportData() {
      this.isExporting = true;
      try {
        // Create CSV data
        const headers = ['Company', 'Plan', 'Status', 'Start Date', 'End Date', 'Duration', 'Monthly Price', 'Reason'];
        const csvData = this.subscriptions.map(sub => [
          sub.company_name,
          sub.subscription_plan?.name || '',
          this.formatStatus(sub.status),
          this.formatDate(sub.start_date),
          this.formatDate(sub.end_date),
          this.calculateDuration(sub.start_date, sub.end_date),
          sub.subscription_plan?.monthly_price || 0,
          sub.notes || this.getReasonText(sub.status)
        ]);
        
        const csvContent = [headers, ...csvData]
          .map(row => row.map(field => `"${field}"`).join(','))
          .join('\n');
        
        // Download CSV
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `past-subscriptions-${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
        
        this.$emit('show-message', {
          type: 'success',
          message: 'Data exported successfully'
        });
      } catch (error) {
        console.error('Error exporting data:', error);
        this.$emit('show-message', {
          type: 'error',
          message: 'Failed to export data'
        });
      }
      this.isExporting = false;
    }
  }
};
</script>

<style scoped>
.past-subscriptions {
  min-height: calc(100vh - 64px);
}

/* Page Header */
.page-header {
  background: white;
  border-radius: 20px;
  padding: 16px 20px;
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
  position: relative;
  overflow: hidden;
}

.page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.page-inline-subtitle {
  font-size: 16px;
  color: #64748b;
  font-weight: 400;
  margin-left: 16px;
  display: inline;
}

.vibrant-title-icon {
  background: linear-gradient(135deg, #ff6b6b 0%, #4ecdc4 25%, #667eea 50%, #764ba2 75%, #f093fb 100%) !important;
  -webkit-background-clip: text !important;
  background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  font-weight: 900 !important;
  filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4));
  font-size: 32px !important;
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.modern-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  padding: 0 20px !important;
  height: 44px !important;
  font-size: 14px !important;
  transition: all 0.3s ease;
}

.modern-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4) !important;
}

/* Stats Cards */
.stats-card {
  border-radius: 12px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  transition: all 0.3s ease;
}

.stats-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.15) !important;
}

.stats-icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stats-icon-wrapper.error {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.stats-icon-wrapper.warning {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.stats-icon-wrapper.grey {
  background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
}

.stats-icon-wrapper.info {
  background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
}

.stats-value {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  line-height: 1;
}

.stats-label {
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
  margin-top: 4px;
}

/* Filters Card */
.filters-card {
  border-radius: 12px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
}

.filter-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

/* Table Card */
.subscriptions-table-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  overflow: hidden;
}

.table-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  padding: 20px 24px !important;
}

.table-count {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
}

.count-text {
  background: rgba(255, 255, 255, 0.1);
  padding: 4px 12px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

/* Table Content */
.company-info {
  display: flex;
  align-items: center;
}

.company-name {
  font-weight: 600;
  color: #2c3e50;
}

.plan-info {
  display: flex;
  flex-direction: column;
}

.plan-name {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 2px;
}

.plan-type {
  font-size: 12px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.agents-list {
  max-width: 200px;
}

.billing-info {
  display: flex;
  flex-direction: column;
}

.amount {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 2px;
}

.billing-cycle {
  font-size: 12px;
  color: #64748b;
}

.date-info {
  font-size: 14px;
  color: #2c3e50;
}

.duration-info {
  font-size: 14px;
  color: #2c3e50;
  font-weight: 500;
}

.reason-info {
  max-width: 150px;
}

.text-muted {
  color: #94a3b8;
  font-style: italic;
}

.action-buttons {
  display: flex;
  gap: 4px;
}

/* Minimal Input Styling */
.minimal-input {
  margin-bottom: 0;
}

.minimal-input >>> .v-input__control {
  min-height: 48px;
}

.minimal-input >>> .v-input__control .v-input__slot {
  background: white !important;
  border: 1.5px solid #e2e8f0 !important;
  border-radius: 8px !important;
  transition: all 0.3s ease;
  padding: 0 12px !important;
  margin: 0 !important;
  box-shadow: none !important;
}

.minimal-input >>> .v-input__control .v-input__slot:before,
.minimal-input >>> .v-input__control .v-input__slot:after {
  display: none !important;
}

.minimal-input >>> .v-input__control .v-input__slot:hover {
  border-color: #cbd5e1 !important;
}

.minimal-input >>> .v-input--is-focused .v-input__control .v-input__slot {
  border-color: #667eea !important;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1) !important;
}

.minimal-input >>> .v-label {
  color: #64748b !important;
  font-weight: 500;
  font-size: 14px;
}

.minimal-input >>> .v-label--active {
  color: #667eea !important;
}

.minimal-input >>> input {
  color: #1e293b !important;
  font-weight: 500;
  font-size: 14px;
}

.minimal-input >>> .v-input__prepend-inner .v-icon {
  color: #667eea !important;
  margin-right: 8px;
}

/* Remove fieldset borders */
.minimal-input >>> fieldset {
  display: none !important;
}

/* Edit Dialog Styling */
.edit-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.dialog-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  padding: 24px !important;
  border-radius: 0 !important;
}

.dialog-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.dialog-title-section {
  flex: 1;
}

.dialog-title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: white;
  line-height: 1.2;
}

.dialog-subtitle {
  margin: 4px 0 0 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 400;
}

.close-btn {
  background: rgba(255, 255, 255, 0.1) !important;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
}

.dialog-content {
  background: #f8f9fa;
}

.dialog-actions {
  background: #f8f9fa;
  border-top: 1px solid rgba(102, 126, 234, 0.1);
}

.cancel-btn {
  color: #64748b !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
}

.cancel-btn:hover {
  background: rgba(100, 116, 139, 0.1) !important;
}

.save-btn {
  background: #667eea !important;
  color: white !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
}

.save-btn:hover {
  background: #5a6fd8 !important;
}

.save-btn:before {
  background-color: transparent;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
}

.empty-state h2 {
  margin: 16px 0 8px 0;
  color: #2c3e50;
}

/* Responsive Design */
@media (max-width: 960px) {
  .page-inline-subtitle {
    display: block;
    margin-left: 0;
    margin-top: 4px;
    font-size: 14px;
  }
  
  .header-content {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
  
  .page-title {
    font-size: 22px;
  }
  
  .filter-actions {
    flex-direction: column;
    gap: 8px;
  }
}

@media (max-width: 600px) {
  .page-title {
    font-size: 20px;
  }
  
  .stats-value {
    font-size: 20px;
  }
  
  .stats-icon-wrapper {
    width: 40px;
    height: 40px;
  }
  
  .filter-actions > .v-btn {
    width: 100%;
  }
}
</style>